// src/pages/PasswordResetPage.jsx
//
// Step 1: enter email, click "Send reset link" → triggers the Microsoft B2C
//   reset-password redirect (the actual password reset happens on Microsoft's
//   hosted UX, so the client never sees an "email sent" event from our side).
// Step 2: shown on the way to Microsoft (or as a fallback if the redirect
//   doesn't take immediately) — matches the design's "Check your inbox" state.
import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import { loginRequest, b2cPolicies } from "../authConfig";
import AuthSplitLayout from "../components/auth/AuthSplitLayout";
import Eyebrow from "../components/brand/Eyebrow";
import { fonts } from "../theme/tokens";

export default function PasswordResetPage() {
  const theme = useTheme();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const triggerReset = async () => {
    if (inProgress !== InteractionStatus.None) return;
    setError("");
    setSubmitting(true);
    try {
      // Visually advance to step 2 before the redirect — most users won't see
      // this because the redirect takes them off-domain immediately, but it
      // covers the fallback case.
      setStep(1);
      await instance.loginRedirect({
        ...loginRequest,
        authority: b2cPolicies.authorities.resetPassword.authority,
        loginHint: email || undefined,
      });
    } catch (e) {
      console.error("Reset redirect error:", e);
      setError("Couldn't start the reset flow. Please try again.");
      setStep(0);
      setSubmitting(false);
    }
  };

  return (
    <AuthSplitLayout
      leftEyebrow="Account recovery"
      leftTitle={
        <>
          We'll have you<br />
          <Box
            component="em"
            sx={{ fontStyle: "italic", color: theme.palette.brand.gold }}
          >
            back
          </Box>{" "}
          in moments.
        </>
      }
      leftSubtitle={
        <>
          A one-time reset link will be sent via Microsoft to your work email.
          Links expire 30 minutes after they're issued.
        </>
      }
    >
      <Box
        component={RouterLink}
        to="/login"
        sx={{
          fontSize: 13,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          color: theme.palette.brand.inkMuted,
          textDecoration: "none",
          mb: 3,
          "&:hover": { color: theme.palette.brand.ink },
        }}
      >
        <ArrowBackRoundedIcon sx={{ fontSize: 16 }} /> Back to sign in
      </Box>

      {step === 0 ? (
        <>
          <Eyebrow sx={{ color: theme.palette.brand.goldDeep, mb: 1.5 }}>
            Step 1 of 2
          </Eyebrow>
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 36,
              fontWeight: 500,
              lineHeight: 1.1,
              m: 0,
              mb: 1,
              color: theme.palette.brand.ink,
            }}
          >
            Reset password
          </Typography>
          <Typography sx={{ fontSize: 14, color: theme.palette.brand.inkMuted, mb: 3.5 }}>
            Enter the work email linked to your account.
          </Typography>

          <Eyebrow
            sx={{
              display: "block",
              color: theme.palette.brand.inkSoft,
              mb: 0.75,
              fontSize: 10,
            }}
          >
            Email
          </Eyebrow>
          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@xl-property.co.uk"
            autoFocus
            error={Boolean(error)}
            helperText={error || " "}
            FormHelperTextProps={{ sx: { mx: 0 } }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={triggerReset}
            disabled={submitting}
            sx={{ mt: 1.5, py: 1.4, gap: 1 }}
          >
            {submitting ? (
              <>
                <CircularProgress size={14} sx={{ color: "#fff" }} /> Redirecting…
              </>
            ) : (
              <>
                Send reset link <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
              </>
            )}
          </Button>
        </>
      ) : (
        <>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: theme.palette.brand.goldSoft,
              color: theme.palette.brand.goldDeep,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.75,
            }}
          >
            <MailOutlineRoundedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Eyebrow sx={{ color: theme.palette.brand.goldDeep, mb: 1.5 }}>
            Step 2 of 2
          </Eyebrow>
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 36,
              fontWeight: 500,
              lineHeight: 1.1,
              m: 0,
              mb: 1,
              color: theme.palette.brand.ink,
            }}
          >
            Check your inbox
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: theme.palette.brand.inkMuted,
              mb: 2.75,
              lineHeight: 1.65,
            }}
          >
            We've sent a link to{" "}
            <Box component="span" sx={{ color: theme.palette.brand.ink, fontWeight: 600 }}>
              {email || "your email"}
            </Box>
            . The link expires in 30 minutes.
          </Typography>

          <Box
            sx={{
              p: 1.75,
              backgroundColor: theme.palette.brand.surfaceMuted,
              borderRadius: "6px",
              fontSize: 13,
              color: theme.palette.brand.inkMuted,
              lineHeight: 1.55,
              border: `1px solid ${theme.palette.brand.border}`,
              mb: 2.25,
            }}
          >
            <Box component="strong" sx={{ color: theme.palette.brand.ink, fontWeight: 600 }}>
              Didn't receive it?
            </Box>{" "}
            Check your spam folder, or{" "}
            <Box
              component="span"
              onClick={() => {
                setStep(0);
                setSubmitting(false);
              }}
              sx={{
                color: theme.palette.brand.ink,
                textDecoration: "underline",
                textDecorationColor: theme.palette.brand.gold,
                textUnderlineOffset: "3px",
                cursor: "pointer",
              }}
            >
              try again
            </Box>
            .
          </Box>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate("/login")}
            sx={{ py: 1.4 }}
          >
            Return to sign in
          </Button>
        </>
      )}
    </AuthSplitLayout>
  );
}
